import {
  darkThemeStyles,
  Group,
  JupiterTheme,
  MockHook,
} from '@jupiterone/jupiter-web-toolkit';
import {
  Box,
  Button,
  CircularProgress,
  List,
  TextField,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { ChangeEvent, useMemo } from 'react';
import { Virtuoso, Components } from 'react-virtuoso';
import GroupListItem from '../GroupListItem';
import useLazyLoadListLogic, {
  LazyLoadListComponentProps,
} from './useLazyLoadListLogic';

const LOADER_BACKGROUND =
  'linear-gradient(to bottom, rgba(0,0,0,0%) 0%,rgba(0,0,0,0.65) 100%)';

const useStyles = makeStyles((theme: JupiterTheme) => {
  return createStyles({
    ...darkThemeStyles,
    lazyLoadedListContainer: {
      position: 'relative',
      height: 'calc(100% - 90px)',
    },
    listCustomScrollbar: {
      '&::-webkit-scrollbar': {
        background: `rgba(8, 34, 76, .875)`,
        borderRadius: '0 4px 4px 0',
      },
      '&::-webkit-scrollbar-thumb': {
        background: `rgba(0, 6, 42, .875)`,
        borderRadius: '0 4px 4px 0',
      },
    },
    lazyLoadedList: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      marginTop: '40px',
      width: '100%',
    },
    lazyLoadedListLoaderContainer: {
      position: 'absolute',
      width: '100%',
      bottom: 0,
      background: LOADER_BACKGROUND,
      display: 'flex',
      padding: theme.spacing(8, 0),
      justifyContent: 'center',
    },
    lazyLoadedListCircularProgress: {
      color: theme.palette.coolGrey[100],
    },
    usersAndAccess: {
      height: '100%',
      width: '100%',
    },
    groupPanelSubheaderText: {
      color: theme.palette.coolGrey[50],
      fontSize: '1.5rem',
      fontWeight: 900,
    },
    headerLayout: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1.5),
      '&:not(:first-child)': {
        marginTop: theme.spacing(4),
      },
      '& *': {
        outline: 'none',
      },
      '& $button': {
        borderColor: theme.palette.primary.contrastText,
        color: theme.palette.primary.contrastText,
        paddingRight: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        '&:hover, &:focus': {
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
        },
      },
      '& h3': {
        lineHeight: 1,
        marginTop: -1,
      },
    },
  });
});

export const LazyLoadListComponent = ({
  addButtonText,
  filter,
  filteredList,
  isLoading,
  loadMoreGroups,
  onAddGroupClick,
  onSelectGroup,
  setFilter,
  titleText,
}: LazyLoadListComponentProps) => {
  const classes = useStyles();

  const updateFilter = (e: ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
  };

  const renderGroup = (index: number, group: Group) => {
    return (
      <GroupListItem
        item={group}
        key={`${group.id}-${index}`}
        onSelect={onSelectGroup}
      />
    );
  };

  const virtuosoComponents = useMemo((): Components => {
    return {
      List: React.forwardRef(function _List({ children, style }, listRef) {
        return (
          <List
            className={classes.lazyLoadedList}
            style={{ ...style }}
            component="div"
            ref={listRef}
          >
            {children}
          </List>
        );
      }),
    };
  }, []);

  return (
    <div className={classes.usersAndAccess}>
      <div className={classes.headerLayout}>
        <Typography variant="h6" className={classes.groupPanelSubheaderText}>
          {titleText}
        </Typography>
        <Button variant="outlined" size="small" onClick={onAddGroupClick}>
          {addButtonText}
        </Button>
      </div>
      <form
        className={classes.headerLayout}
        noValidate
        autoComplete="off"
        onSubmit={(e) => e.preventDefault()}
      >
        <TextField
          id="outlined-basic"
          label="Search"
          variant="filled"
          onChange={updateFilter}
          InputLabelProps={{
            className: classes.textInputLabel,
            classes: {
              focused: classes.textInputLabelFocused,
            },
          }}
          InputProps={{
            className: classes.textInput,
          }}
          value={filter}
          fullWidth
        />
      </form>
      <div className={classes.lazyLoadedListContainer}>
        {isLoading ? (
          <Box className={classes.lazyLoadedListLoaderContainer}>
            <CircularProgress
              className={classes.lazyLoadedListCircularProgress}
            />
          </Box>
        ) : null}
        <Virtuoso
          className={classes.listCustomScrollbar}
          components={virtuosoComponents}
          endReached={loadMoreGroups}
          overscan={200}
          data={filteredList}
          itemContent={renderGroup}
        />
      </div>
    </div>
  );
};

export default MockHook.withHookHoc(
  LazyLoadListComponent,
  useLazyLoadListLogic
);
